import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ArrowLeft, Sparkles, Zap, Shield, Ghost, Snowflake, RefreshCw, Clock, BookOpen, Trophy, Brain, Target } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { ChessAppLayout } from '@/components/ChessAppLayout';
import { useSEO } from '@/hooks/useSEO';
import { SUPERPOWERS, ORB_DEFS } from '@/lib/arcane-blitz-engine';

const POWER_ICONS: Record<string, React.ReactNode> = {
  'nova-strike':    <Zap className="w-5 h-5 text-teen-gold" />,
  'arcane-shield':  <Shield className="w-5 h-5 text-teen-cyan" />,
  'phantom-step':   <Ghost className="w-5 h-5 text-teen-purple" />,
  'hex-freeze':     <Snowflake className="w-5 h-5 text-teen-blue" />,
  'soul-swap':      <RefreshCw className="w-5 h-5 text-teen-pink" />,
  'time-warp':      <Clock className="w-5 h-5 text-teen-orange" />,
};

const TIPS = [
  {
    title: 'Save mana for combos',
    desc: 'Collecting a Mana Gem (💎) and then immediately firing a Phantom Step catches opponents off-guard.',
    icon: <Brain className="w-5 h-5 text-teen-purple" />,
    level: 'Beginner',
  },
  {
    title: 'Freeze before a fork',
    desc: 'Hex-Freeze (❄️) the most dangerous defender, then execute a fork or skewer on the next move.',
    icon: <Target className="w-5 h-5 text-teen-cyan" />,
    level: 'Intermediate',
  },
  {
    title: 'Soul Swap for surprise attacks',
    desc: 'Swap a passive piece with an attacking one to create an unstoppable discovered check.',
    icon: <Trophy className="w-5 h-5 text-teen-gold" />,
    level: 'Advanced',
  },
  {
    title: 'Time Warp as insurance',
    desc: 'Save 5 mana all game — then undo the one move that costs you material. Used defensively, it swings games.',
    icon: <Sparkles className="w-5 h-5 text-teen-pink" />,
    level: 'Expert',
  },
];

export default function ArcaneBlitzRules() {
  const navigate = useNavigate();

  useSEO({
    title: 'Arcane Blitz Rules – Superpower Chess | ChessHatch',
    description: 'Learn how to play Arcane Blitz, the superpower chess mode! Master 6 devastating powers and 4 power-up orbs to dominate the magical board.',
    keywords: 'arcane blitz chess, superpower chess, mana chess, chess variant, power up chess',
    canonical: '/arcane-blitz-rules',
  });

  return (
    <ChessAppLayout>
      <div className="min-h-screen bg-gradient-to-br from-violet-950 via-purple-900/40 to-indigo-950 relative overflow-hidden">
        {/* Particles */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-16 left-12 text-teen-cyan text-2xl animate-float-particle opacity-60">✨</div>
          <div className="absolute top-32 right-20 text-teen-pink text-3xl animate-float-particle opacity-50" style={{ animationDelay: '1s' }}>⭐</div>
          <div className="absolute bottom-40 left-1/4 text-teen-gold text-2xl animate-float-particle opacity-50" style={{ animationDelay: '2s' }}>💫</div>
          <div className="absolute bottom-20 right-1/3 text-teen-purple text-2xl animate-float-particle opacity-40" style={{ animationDelay: '3s' }}>✦</div>
        </div>

        {/* Header */}
        <div className="border-b border-teen-purple/30 bg-violet-950/70 backdrop-blur-sm">
          <div className="container mx-auto px-4 py-5 flex items-center justify-between">
            <Button variant="ghost" onClick={() => navigate('/')}
              className="text-foreground/80 hover:text-foreground flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" /> Back to Home
            </Button>
            <Button
              onClick={() => navigate('/?mode=arcane')}
              className="bg-gradient-to-r from-teen-purple via-teen-pink to-teen-cyan text-white font-semibold shadow-lg hover:opacity-90"
            >
              <Sparkles className="w-4 h-4 mr-2" /> Play Arcane Blitz
            </Button>
          </div>
        </div>

        <div className="container mx-auto px-4 py-10 max-w-5xl relative z-10">
          {/* Hero */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-3 mb-4">
              <Sparkles className="w-8 h-8 text-teen-cyan animate-sparkle" />
              <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-teen-cyan via-teen-pink to-teen-purple bg-clip-text text-transparent">
                Arcane Blitz
              </h1>
              <Sparkles className="w-8 h-8 text-teen-pink animate-sparkle" />
            </div>
            <p className="text-lg text-foreground/80 max-w-2xl mx-auto">
              Chess meets sorcery. Earn <strong className="text-teen-cyan">Mana</strong> every move,
              unleash <strong className="text-teen-pink">Superpowers</strong>, and collect
              <strong className="text-teen-gold"> Power-Up Orbs</strong> to obliterate your opponent.
            </p>
          </div>

          {/* Mana system */}
          <Card className="mb-8 border-teen-cyan/30 bg-gradient-to-br from-violet-950/60 via-purple-900/40 to-indigo-950/60 animate-aurora-glow">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-teen-cyan">
                <Zap className="w-5 h-5" /> The Mana System
              </CardTitle>
              <CardDescription className="text-foreground/70">Your fuel for casting superpowers</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div className="p-4 rounded-lg bg-teen-cyan/10 border border-teen-cyan/20 text-center">
                  <p className="text-2xl mb-1">⚡ +1</p>
                  <p className="font-semibold text-foreground">Every move</p>
                  <p className="text-foreground/60 text-xs mt-1">Just making a move earns you mana</p>
                </div>
                <div className="p-4 rounded-lg bg-teen-pink/10 border border-teen-pink/20 text-center">
                  <p className="text-2xl mb-1">⚡ +2</p>
                  <p className="font-semibold text-foreground">Capture a piece</p>
                  <p className="text-foreground/60 text-xs mt-1">Captures reward bonus mana</p>
                </div>
                <div className="p-4 rounded-lg bg-teen-gold/10 border border-teen-gold/20 text-center">
                  <p className="text-2xl mb-1">⚡ 3</p>
                  <p className="font-semibold text-foreground">Starting mana</p>
                  <p className="text-foreground/60 text-xs mt-1">Both players begin with 3 mana (max 10)</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Superpowers */}
          <Card className="mb-8 border-teen-purple/30 bg-violet-950/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-teen-pink">
                <Sparkles className="w-5 h-5" /> The 6 Superpowers
              </CardTitle>
              <CardDescription className="text-foreground/70">Click a power button during your turn, then click the target on the board</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {SUPERPOWERS.map(def => (
                  <div key={def.type} className="p-4 rounded-lg border border-teen-purple/20 bg-violet-950/40 hover:border-teen-purple/40 transition-colors">
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-lg bg-violet-900/60 shrink-0">
                        {POWER_ICONS[def.type]}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-bold text-foreground">{def.emoji} {def.name}</span>
                          <Badge variant="outline" className="text-xs border-teen-cyan/40 text-teen-cyan">
                            {def.cost} mana
                          </Badge>
                        </div>
                        <p className="text-sm text-foreground/70">{def.description}</p>
                        {def.needsDoubleTarget && (
                          <Badge variant="secondary" className="text-xs mt-1 bg-teen-gold/10 text-teen-gold border-teen-gold/30">
                            2 clicks needed
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Power Orbs */}
          <Card className="mb-8 border-teen-gold/30 bg-violet-950/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-teen-gold">
                💎 Power-Up Orbs
              </CardTitle>
              <CardDescription className="text-foreground/70">
                Spawn on random empty squares. Walk your piece onto them to collect. Respawn every 8 moves.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {ORB_DEFS.map(orb => (
                  <div key={orb.type} className="p-4 rounded-lg border border-teen-gold/20 bg-violet-950/40 flex items-start gap-3 hover:border-teen-gold/40 transition-colors">
                    <span className="text-2xl leading-none">{orb.emoji}</span>
                    <div>
                      <p className={`font-semibold ${orb.colorClass}`}>{orb.name}</p>
                      <p className="text-sm text-foreground/70">{orb.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Tips */}
          <Card className="mb-8 border-teen-green/20 bg-violet-950/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-teen-green">
                <BookOpen className="w-5 h-5" /> Strategy Tips
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {TIPS.map((tip, i) => (
                  <div key={i} className="flex items-start gap-3 p-4 rounded-lg bg-violet-950/40 border border-teen-green/10">
                    <div className="p-2 rounded-lg bg-violet-900/60 shrink-0">{tip.icon}</div>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-semibold text-foreground">{tip.title}</span>
                        <Badge variant="outline" className="text-xs">{tip.level}</Badge>
                      </div>
                      <p className="text-sm text-foreground/70">{tip.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* CTA */}
          <div className="text-center py-8">
            <Button
              size="lg"
              onClick={() => navigate('/?mode=arcane')}
              className="bg-gradient-to-r from-teen-purple via-teen-pink to-teen-cyan text-white font-bold text-lg px-10 py-6 shadow-lg hover:opacity-90 hover:scale-105 transition-all"
            >
              <Sparkles className="w-5 h-5 mr-2" />
              Enter the Arcane Realm ⚡
            </Button>
          </div>
        </div>
      </div>
    </ChessAppLayout>
  );
}
